import { formatWholeNumber } from '../util/numberFormat';

// Retroalimentación pedagógica de Resta: explica el ejercicio real seleccionado
// en Práctica, sin alterar el banco ni generar preguntas nuevas.
const PLACE_NAMES = ['unidades', 'decenas', 'centenas', 'unidades de millar'];

const digitCount = value => Math.max(1, String(Math.abs(value)).length);

const paddedDigits = (value, width) => {
  const digits = String(Math.abs(value)).split('');
  return Array.from({ length: width }, (_, index) => {
    const sourceIndex = index - (width - digits.length);
    return sourceIndex >= 0 ? digits[sourceIndex] : '';
  });
};

const placeForGridIndex = (gridIndex, width) => PLACE_NAMES[width - 1 - gridIndex] || `posición ${width - gridIndex}`;

const singularPlace = place => place === 'unidades de millar'
  ? 'unidad de millar'
  : place.endsWith('s') ? place.slice(0, -1) : place;

const joinSpanish = values => {
  if (values.length <= 1) return values[0] || '';
  if (values.length === 2) return values[0] + ' y ' + values[1];
  return values.slice(0, -1).join(', ') + ' y ' + values.at(-1);
};

const describeWorkingDigits = (digits, width) => {
  const firstVisible = digits.findIndex(value => Number(value) !== 0);
  const start = firstVisible === -1 ? width - 1 : firstVisible;
  return joinSpanish(digits
    .slice(start)
    .map((value, index) => {
      const gridIndex = start + index;
      return formatWholeNumber(value) + ' ' + placeForGridIndex(gridIndex, width);
    }));
};

export function classifySubtractionProblem(problem) {
  const width = Math.max(digitCount(problem.a), digitCount(problem.b));
  const minuend = paddedDigits(problem.a, width).map(Number);
  const subtrahend = paddedDigits(problem.b, width).map(Number);
  const working = [...minuend];
  let hasRegrouping = false;

  for (let gridIndex = width - 1; gridIndex >= 0; gridIndex -= 1) {
    if (working[gridIndex] >= subtrahend[gridIndex]) continue;
    hasRegrouping = true;
    let sourceIndex = gridIndex - 1;
    while (sourceIndex >= 0 && working[sourceIndex] === 0) sourceIndex -= 1;
    if (sourceIndex < 0) break;
    for (let index = sourceIndex; index < gridIndex; index += 1) {
      working[index] -= 1;
      working[index + 1] += 10;
    }
  }

  if (width === 1) return 'single';
  return `${width}_${hasRegrouping ? 'regroup' : 'no_regroup'}`;
}

function buildSingleDigitTutorial(problem) {
  const start = problem.a;
  const jumps = problem.b;
  const result = problem.answer;
  const jumpLabel = jumps === 1 ? 'salto' : 'saltos';
  const isZeroSubtraction = jumps === 0;

  return {
    kind: 'number-line-subtraction',
    a: problem.a,
    b: problem.b,
    result,
    numberLineMax: 20,
    steps: [
      {
        id: 'start',
        label: 'Paso 1',
        title: 'Empieza en ' + formatWholeNumber(start) + '.',
        message: 'Comenzamos en el primer número de la resta.',
        showJumps: false,
        showResult: false,
      },
      {
        id: 'count',
        label: 'Paso 2',
        title: isZeroSubtraction
          ? 'No damos ningún salto.'
          : 'Da ' + formatWholeNumber(jumps) + ' ' + jumpLabel + ' a la izquierda.',
        message: isZeroSubtraction
          ? 'No damos ningún salto porque restamos 0.'
          : 'Damos ' + formatWholeNumber(jumps) + ' ' + jumpLabel + ' a la izquierda desde el ' + formatWholeNumber(start) + '.',
        showJumps: true,
        showResult: false,
      },
      {
        id: 'result',
        label: 'Resultado',
        title: isZeroSubtraction
          ? 'Nos quedamos en ' + formatWholeNumber(start) + '.'
          : 'Llegamos al ' + formatWholeNumber(result) + '.',
        message: 'Entonces, ' + formatWholeNumber(problem.a) + ' − ' + formatWholeNumber(problem.b) + ' = ' + formatWholeNumber(result) + '.',
        showJumps: true,
        showResult: true,
        result,
      },
    ],
  };
}

function buildColumnTutorial(problem) {
  const width = Math.max(digitCount(problem.a), digitCount(problem.b));
  const minuend = paddedDigits(problem.a, width).map(Number);
  const subtrahend = paddedDigits(problem.b, width).map(Number);
  const result = problem.answer;
  const resultDigits = paddedDigits(result, width);
  const working = [...minuend];
  const steps = [];
  let stepNumber = 1;

  steps.push({
    id: 'align',
    label: 'Paso ' + stepNumber,
    title: 'Alinea los números por valor posicional.',
    message: width === 2
      ? 'Las decenas quedan debajo de las decenas y las unidades debajo de las unidades.'
      : 'Las centenas, decenas y unidades deben quedar en la misma columna.',
    activeColumn: null,
    minuendDigits: working.map(String),
    resultVisible: [],
    regroupedColumns: [],
  });
  stepNumber += 1;

  for (let gridIndex = width - 1; gridIndex >= 0; gridIndex -= 1) {
    const subtrahendDigit = subtrahend[gridIndex];

    if (working[gridIndex] < subtrahendDigit) {
      const originalDigit = working[gridIndex];
      let sourceIndex = gridIndex - 1;
      while (sourceIndex >= 0 && working[sourceIndex] === 0) sourceIndex -= 1;

      if (sourceIndex >= 0) {
        for (let index = sourceIndex; index < gridIndex; index += 1) {
          const sourcePlace = placeForGridIndex(index, width);
          const targetPlace = placeForGridIndex(index + 1, width);
          working[index] -= 1;
          working[index + 1] += 10;

          steps.push({
            id: 'regroup-' + gridIndex + '-' + index,
            label: 'Paso ' + stepNumber,
            title: 'Reagrupa 1 ' + singularPlace(sourcePlace) + ' como 10 ' + targetPlace + '.',
            message: (index === sourceIndex
              ? 'Como ' + formatWholeNumber(originalDigit) + ' < ' + formatWholeNumber(subtrahendDigit) + ', reagrupamos 1 ' + singularPlace(sourcePlace) + ' como 10 ' + targetPlace + '. '
              : '') + 'Ahora tenemos ' + describeWorkingDigits(working, width) + '.',
            activeColumn: index + 1,
            minuendDigits: working.map(String),
            resultVisible: [],
            regroupedColumns: [index, index + 1],
          });
          stepNumber += 1;
        }
      }
    }

    const topDigit = working[gridIndex];
    const difference = topDigit - subtrahendDigit;
    const solvedColumns = Array.from({ length: width - gridIndex }, (_, index) => width - 1 - index);

    steps.push({
      id: 'subtract-' + gridIndex,
      label: 'Paso ' + stepNumber,
      title: 'Resta las ' + placeForGridIndex(gridIndex, width) + '.',
      message: formatWholeNumber(topDigit) + ' − ' + formatWholeNumber(subtrahendDigit) + ' = ' + formatWholeNumber(difference) + '. Escribe ' + formatWholeNumber(difference) + ' en las ' + placeForGridIndex(gridIndex, width) + '.',
      activeColumn: gridIndex,
      minuendDigits: working.map(String),
      resultVisible: solvedColumns,
      regroupedColumns: [],
    });
    stepNumber += 1;
  }

  steps.push({
    id: 'result',
    label: 'Resultado',
    title: formatWholeNumber(problem.a) + ' − ' + formatWholeNumber(problem.b) + ' = ' + formatWholeNumber(result),
    message: 'Ya restaste cada columna por valor posicional.',
    activeColumn: null,
    minuendDigits: working.map(String),
    resultVisible: Array.from({ length: width }, (_, index) => index),
    regroupedColumns: [],
    result,
  });

  return {
    kind: 'columns-subtraction',
    a: problem.a,
    b: problem.b,
    result,
    gridWidth: width,
    resultDigits,
    steps,
  };
}

// Mantiene Planeta 1 exactamente en recta numérica. Planetas 2–6 se
// seleccionan dinámicamente por cantidad de dígitos y reagrupación.
export function buildSubtractionWorkedExample(problem) {
  return classifySubtractionProblem(problem) === 'single'
    ? buildSingleDigitTutorial(problem)
    : buildColumnTutorial(problem);
}
